import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes
} from 'firebase/storage';
import { db, storage } from '../../../shared/utils/firebase';
import type {
  DriveResource,
  DriveResourceCreateInput,
  DriveResourceType,
  DriveResourceUpdateInput,
  FirestoreDriveResource,
  DriveSectionId
} from '../types';

const DRIVE_COLLECTION = 'drive_resources';

const driveCollectionRef = collection(db, DRIVE_COLLECTION);

const pruneUndefined = <T extends Record<string, unknown>>(payload: T): T => {
  const cleanedEntries = Object.entries(payload).filter(([, value]) => value !== undefined);
  return Object.fromEntries(cleanedEntries) as T;
};

const assertFilePresent = (file: File | undefined, resourceType: DriveResourceType) => {
  if (resourceType === 'storage' && !file) {
    throw new Error('File is required when resource type is storage');
  }
};

const toPlainDate = (value: unknown | undefined) => {
  if (!value) {
    return undefined;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as any).toDate === 'function') {
    try {
      return (value as { toDate: () => Date }).toDate();
    } catch (error) {
      console.error('Failed to convert Firestore timestamp', error);
      return undefined;
    }
  }
  return undefined;
};

export const mapDocToDriveResource = (
  id: string,
  data: FirestoreDriveResource
): DriveResource => {
  const base = {
    id,
    title: data.title,
    description: data.description,
    section: data.section,
    resourceType: data.resourceType,
    tags: data.tags,
    pinWeight: data.pinWeight,
    createdAt: toPlainDate(data.createdAt),
    createdBy: data.createdBy,
    lastUpdatedAt: toPlainDate(data.lastUpdatedAt),
    lastUpdatedBy: data.lastUpdatedBy
  };

  if (data.resourceType === 'storage') {
    return {
      ...base,
      resourceType: 'storage',
      storagePath: data.storagePath || '',
      storageBucket: data.storageBucket || '',
      fileName: data.fileName || '',
      fileSize: data.fileSize || 0,
      fileType: data.fileType || 'application/octet-stream',
      downloadUrl: data.downloadUrl
    };
  }

  if (data.resourceType === 'google-drive') {
    return {
      ...base,
      resourceType: 'google-drive',
      externalUrl: data.externalUrl || '',
      googleDriveId: data.googleDriveId
    };
  }

  return {
    ...base,
    resourceType: 'external',
    externalUrl: data.externalUrl || ''
  };
};

export const listenToSectionResources = (
  section: DriveSectionId,
  onData: (resources: DriveResource[]) => void,
  onError: (error: Error) => void
) => {
  const sectionQuery = query(
    driveCollectionRef,
    where('section', '==', section),
    orderBy('pinWeight', 'desc'),
    orderBy('title', 'asc')
  );

  return onSnapshot(
    sectionQuery,
    (snapshot) => {
      const resources = snapshot.docs.map((docSnapshot) =>
        mapDocToDriveResource(docSnapshot.id, docSnapshot.data() as FirestoreDriveResource)
      );
      onData(resources);
    },
    (error) => onError(error as Error)
  );
};

const uploadDriveFile = async (
  section: DriveSectionId,
  resourceId: string,
  file: File
) => {
  const path = `drive/${section}/${resourceId}/${file.name}`;
  const fileRef = ref(storage, path);
  const result = await uploadBytes(fileRef, file, {
    contentType: file.type
  });
  const downloadUrl = await getDownloadURL(result.ref);

  return {
    storagePath: result.ref.fullPath,
    storageBucket: result.ref.bucket,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    downloadUrl
  };
};

const deleteDriveFile = async (storagePath?: string) => {
  if (!storagePath) return;
  try {
    const objectRef = ref(storage, storagePath);
    await deleteObject(objectRef);
  } catch (error) {
    console.warn('Failed to delete Drive file from storage', error);
  }
};

export const createDriveResource = async (
  input: DriveResourceCreateInput,
  userId: string
) => {
  assertFilePresent(input.file, input.resourceType);

  const docRef = doc(driveCollectionRef);
  const metadata: Partial<FirestoreDriveResource> = {
    title: input.title,
    description: input.description,
    section: input.section,
    resourceType: input.resourceType,
    tags: input.tags || [],
    pinWeight: input.pinWeight ?? 0,
    createdAt: serverTimestamp(),
    createdBy: userId,
    lastUpdatedAt: serverTimestamp(),
    lastUpdatedBy: userId
  };

  if (input.resourceType === 'storage') {
    const uploadMeta = await uploadDriveFile(input.section, docRef.id, input.file as File);
    Object.assign(metadata, uploadMeta);
  } else {
    metadata.externalUrl = input.externalUrl || '';
    if (input.resourceType === 'google-drive') {
      metadata.googleDriveId = input.googleDriveId || undefined;
    }
  }

  await setDoc(docRef, pruneUndefined(metadata));
  const snapshot = await getDoc(docRef);
  return mapDocToDriveResource(docRef.id, snapshot.data() as FirestoreDriveResource);
};

export const updateDriveResource = async (
  input: DriveResourceUpdateInput,
  userId: string
) => {
  const docRef = doc(db, DRIVE_COLLECTION, input.id);
  const existingSnapshot = await getDoc(docRef);

  if (!existingSnapshot.exists()) {
    throw new Error('Resource not found');
  }

  const existingData = existingSnapshot.data() as FirestoreDriveResource;
  const updates: Partial<FirestoreDriveResource> = {
    lastUpdatedAt: serverTimestamp(),
    lastUpdatedBy: userId
  };

  if (typeof input.title === 'string') updates.title = input.title;
  if (typeof input.description === 'string') updates.description = input.description;
  if (typeof input.pinWeight === 'number') updates.pinWeight = input.pinWeight;
  if (Array.isArray(input.tags)) updates.tags = input.tags;

  if (existingData.resourceType === 'storage') {
    if (input.file) {
      await deleteDriveFile(existingData.storagePath);
      const uploadMeta = await uploadDriveFile(existingData.section, docRef.id, input.file);
      Object.assign(updates, uploadMeta);
    }
    if (input.file === null) {
      throw new Error('Cannot clear file for storage-backed resource');
    }
  } else {
    if (typeof input.externalUrl === 'string') {
      updates.externalUrl = input.externalUrl;
    }
    if (existingData.resourceType === 'google-drive' && typeof input.googleDriveId === 'string') {
      updates.googleDriveId = input.googleDriveId;
    }
  }

  await updateDoc(docRef, pruneUndefined(updates));
  const snapshot = await getDoc(docRef);
  return mapDocToDriveResource(docRef.id, snapshot.data() as FirestoreDriveResource);
};

export const deleteDriveResource = async (id: string) => {
  const docRef = doc(db, DRIVE_COLLECTION, id);
  const snapshot = await getDoc(docRef);

  if (snapshot.exists()) {
    const data = snapshot.data() as FirestoreDriveResource;
    if (data.resourceType === 'storage') {
      await deleteDriveFile(data.storagePath);
    }
  }

  await deleteDoc(docRef);
};
